/**
 * `/studio` — the staff portal's layout route.
 *
 * Same shape as `/app`: a guard and a shell. Session 20 hangs the five studio
 * screens and the `clients/$slug` tree under it.
 *
 * Session 35 adds the one notice this portal's own guard can raise. A restricted
 * studio account that followed a link to a manager's page — Analytics, Clients,
 * Money, a work order — is sent to the board with `?notice=manager-only`, for the
 * same reason `/app` does it rather than showing a 403: they did nothing wrong, they
 * followed a link, and the part of the portal that *is* theirs is one redirect away.
 *
 * **The notice lives on the layout, not on the board.** Search is inherited down the
 * tree, so declaring it here means every page under `/studio` can be a redirect
 * target without each one adding a key to its own schema — and `pages/studio/tasks`
 * keeps a `validateSearch` about the board and nothing else.
 *
 * **`routeSearch`, and here it matters as much as it does on `/app`.** This is the
 * layout every studio page hangs under, so a `validateSearch` that threw would take
 * the whole portal down for any address carrying a `notice` this file does not know.
 * A notice nobody recognises is no notice: the page renders without one.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'

import { guardStudioPortal, MANAGER_ONLY_NOTICE, STUDIO_HOME } from '../auth/guards.js'
import { NotFound } from '../errors/NotFound.js'
import { routeSearch } from '../search/useSearchState.js'
import { StudioLayout } from '../shell/PortalLayout.js'

const StudioSearchSchema = z.object({
  notice: z.literal(MANAGER_ONLY_NOTICE).optional(),
})

export const Route = createFileRoute('/studio')({
  validateSearch: routeSearch(StudioSearchSchema),
  beforeLoad: ({ context, location }) => {
    // `pathname` as well as `href`: the tier table is keyed on the route, and a query
    // string is not part of one. `href` stays what a sign-in redirect carries back.
    guardStudioPortal(context.session.principal, location.href, location.pathname)
  },
  /**
   * A path under this portal that does not exist keeps the portal: the nav is
   * still the way out. `__root.tsx`'s own `notFoundComponent` is for an address
   * above both portals, where nothing has said which shell it would be.
   */
  notFoundComponent: NotFound,
  component: StudioPortal,
})

function StudioPortal() {
  const { notice } = Route.useSearch()
  const navigate = useNavigate()

  if (notice !== MANAGER_ONLY_NOTICE) return <StudioLayout />

  return (
    <StudioLayout
      notice={
        <div className="notice" role="status">
          <div className="row">
            <span>
              <b>That page is a studio manager’s.</b> Your account works the task board.
            </span>
            <button
              type="button"
              className="btn btn--sm btn--ghost push"
              onClick={() => {
                void navigate({ to: STUDIO_HOME, search: {}, replace: true })
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      }
    />
  )
}
